// ** Material components
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";

// ** Icons
import { PeopleAltRounded, CloseRounded } from "@mui/icons-material";

// ** Third parties
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

// ** Custom components
import Illustration from "components/illustration";
import Search from "components/filters/search";
import Tree from "./tree";
import ListTable from "./list";
import CustomPagination from "components/CustomPagination";

// ** Context
import {
  EmployeeDirectoryProvider as Provider,
  useEmployeeDirectoryApi,
  useEmployeeDirectoryState,
} from "contexts/employeeDirectoryContext";
import FullLoading from "components/FullLoading";

export default function EmployeeDirectory({
  title,
  open,
  setOpen,
  withoutRole = null,
  excludedEmployees = [],
  handleSubmit,
  showInstructors = true,
  hasEmail = false,
  singleSelect = false,
}) {
  const theme = useTheme();

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.default,
        },
      }}
      keepMounted={false}
    >
      <Provider
        values={{
          withoutRole,
          excludedEmployees,
          showInstructors,
          hasEmail,
        }}
      >
        <Content
          open={open}
          setOpen={setOpen}
          title={title}
          handleSubmit={handleSubmit}
          singleSelect={singleSelect}
        />
      </Provider>
    </Dialog>
  );
}

const Content = ({ title, setOpen, handleSubmit, singleSelect }) => {
  const { checkedEmployees } = useEmployeeDirectoryState();
  const { t } = useTranslation();

  const { data, isLoading, isError } = useEmployeeDirectoryApi();
  const { page, setPage, count, setSearch } = useEmployeeDirectoryState();

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    if (!checkedEmployees.length) {
      toast.error(t("EmployeeDirectory.empty_checked_employees"));
    } else {
      await handleSubmit(checkedEmployees);
    }
  };

  let content = "";
  // What to show while loading
  if (isLoading) content = <FullLoading height="80vh" />;
  // if there is an error
  else if (isError)
    content = <Illustration illustration="error" text="General.error" />;
  // if request is successful but there is no data
  else if (!!data && data.count === 0)
    content = <Illustration illustration="noResult" text="General.no_data" />;
  // if request is successful and there is some data
  else
    content = data?.rows && (
      <>
        <ListTable data={data} singleSelect={singleSelect} />
        <CustomPagination
          currentPage={page}
          setCurrentPage={setPage}
          rowsToShow={count}
          count={data?.count}
        />
      </>
    );

  return (
    <>
      <AppBar
        sx={{
          position: "relative",
          // backgroundColor: alpha(theme.palette.primary.main, 0.06),
          // backgroundColor: theme.palette.background.paper,
          // color: theme.palette.primary.main,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseRounded />
          </IconButton>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ ml: 2, flex: 1 }}
            columnGap={1}
          >
            <PeopleAltRounded sx={{ fontSize: "30" }} />
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          </Stack>

          <Button color="inherit" sx={{ fontSize: 18 }} onClick={handleSave}>
            {t("EmployeeDirectory.save_button")}
          </Button>
        </Toolbar>
      </AppBar>

      <Box pt={2} pb={4} px={8}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Tree />
          <Search
            placeholder={t("EmployeeDirectory.search.placeholder")}
            setSearch={setSearch}
          />
        </Stack>

        {content}
      </Box>
    </>
  );
};
